import qs from 'qs';

import type {
  GetProgramParams,
  MentorsProfilesParams,
  MentorsProfilesResponse,
  MentorProfileParams,
  MentoringCustomAttributesResponse,
  GetMentoringCustomAttributesParams,
  EditMentoringProfileRequestParams,
  UpdateRelationshipRequestParams,
  MentoringProfileFromUserParams,
  GetRelationshipParams,
  CreateMentoringProfileParams,
} from 'hb-react/frontoffice/apps/Mentoring/requests/types';
import type {
  MentorProfile,
  MentoringProfile,
  Program,
  Relationship,
} from 'hb-react/frontoffice/apps/Mentoring/types';
import { axiosInstance } from 'hb-react/shared/utils/axios';

export const getMentorsProfiles = ({
  programId,
  signal,
  params,
}: MentorsProfilesParams) =>
  axiosInstance
    .get<MentorsProfilesResponse>(
      Routes.mentorings_program_mentor_profiles_path(programId),
      {
        signal,
        params,
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: 'brackets',
          }),
      },
    )
    .then(({ data }) => ({
      items: data.data,
      totalItems: data.total_count,
      endCursor: data.end_cursor,
      hasNextPage: data.has_next_page,
    }));

export const getMentorProfile = ({
  mentorId,
  programId,
  topicId,
  signal,
}: MentorProfileParams) =>
  axiosInstance
    .get<MentorProfile>(
      Routes.mentorings_program_mentor_profile_path(programId, mentorId),
      {
        signal,
        params: topicId ? { topic_id: topicId } : undefined,
      },
    )
    .then(({ data }) => data);

export const getMentoringProfileFromUser = ({
  signal,
  topicId,
  routeApi,
}: MentoringProfileFromUserParams) =>
  axiosInstance
    .get<MentoringProfile>(routeApi, {
      signal,
      params: topicId ? { topic_id: topicId } : undefined,
    })
    .then(({ data }) => data)
    .catch((error) => {
      // user has no profile
      if (error?.response?.status === 404) {
        return { status: null };
      }
    });

export const getProgram = ({ programId, signal, topicId }: GetProgramParams) =>
  axiosInstance
    .get<Program>(Routes.mentorings_program_path(programId), {
      params: topicId ? { topic_id: topicId } : undefined,
      signal,
    })
    .then(({ data }) => data);

export const createMentoringProfile = ({
  routeApi,
  payload,
  topicId,
}: CreateMentoringProfileParams) =>
  axiosInstance
    .post(routeApi, {
      ...payload,
      ...(topicId && { topic_id: topicId }),
      paramsSerializer: (params) =>
        qs.stringify(params, {
          arrayFormat: 'brackets',
        }),
    })
    .then(({ data }) => data);

export const editMentoringProfile = ({
  routeApi,
  topicId,
  payload,
}: EditMentoringProfileRequestParams) =>
  axiosInstance
    .put<MentoringProfile>(routeApi, {
      ...payload,
      ...(topicId && { topic_id: topicId }),
      paramsSerializer: (params) =>
        qs.stringify(params, {
          arrayFormat: 'brackets',
        }),
    })
    .then(({ data }) => data);

export const getMentoringCustomAttributes = ({
  routeApi,
  signal,
  afterCursor,
  queryConfig,
  topicId,
  requestedFields = [],
}: GetMentoringCustomAttributesParams) => {
  const params = {
    topic_id: topicId,
    after_cursor: afterCursor,
    ...(queryConfig && {
      query: {
        config: queryConfig,
      },
    }),
    limit: 100,
    requested_fields: requestedFields,
  };

  return axiosInstance
    .get<MentoringCustomAttributesResponse>(routeApi, {
      signal,
      params,
      paramsSerializer: (params) =>
        qs.stringify(params, {
          arrayFormat: 'brackets',
        }),
    })
    .then(({ data }) => data);
};

export const updateRelationshipRequest = ({
  relationshipId,
  programId,
  payload,
  topicId,
}: UpdateRelationshipRequestParams) =>
  axiosInstance
    .put<Relationship>(
      Routes.mentorings_program_relationship_path(programId, relationshipId),
      {
        relationship: {
          ...payload,
        },
        ...(topicId && { topic_id: topicId }),
        paramsSerializer: (params) =>
          qs.stringify(params, {
            arrayFormat: 'brackets',
          }),
      },
    )
    .then(({ data }) => data);

export const getRelationship = ({
  programId,
  relationshipId,
  topicId,
  signal,
}: GetRelationshipParams) =>
  axiosInstance
    .get<Relationship>(
      Routes.mentorings_program_relationship_path(programId, relationshipId),
      {
        params: topicId ? { topic_id: topicId } : undefined,
        signal,
      },
    )
    .then(({ data }) => data);
